/**
 * Recovery kit UI (docs/ux/workflow-simplification.md B1 step 3 and B8): save the kit (Download
 * is primary; *Save in 1Password* appears only when the extension is detected), acknowledge it,
 * read a kit back to sign in, and finish restoring encrypted work from it.
 */
import { encodeOPSaveRequest } from '@1password/save-button'
import { Button } from '@viko/transcript-ui'
import { Check, Download, FileKey } from 'lucide-react'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import {
  downloadRecoveryKit,
  parseRecoveryKit,
  type RecoveryKit,
  recoveryKitSaveRequest,
  recoveryKitText,
} from '../lib/recovery-kit.js'
import { useOnePasswordSave } from './recovery-save.js'

/** Save + acknowledge. Viko can't verify the kit was stored, so the user confirms it. */
export function RecoveryKitCard({
  kit,
  onDone,
  doneLabel = 'I saved it — continue',
}: {
  kit: RecoveryKit
  onDone: () => void
  doneLabel?: string
}) {
  const id = useId()
  const [saved, setSaved] = useState(false)
  const request = encodeOPSaveRequest(recoveryKitSaveRequest(kit)) ?? ''
  const { button, extension, wrapperProps } = useOnePasswordSave(request)
  const detected = extension === 'ready'
  return (
    <div className="grid gap-3">
      <p className="m-0 text-[14px] text-text-3">
        Account id <span className="font-mono text-text-2">{kit.accountId}</span>
      </p>
      <p className="m-0 text-[15px] text-text-2">
        {kit.dataKey
          ? `One file: your data recovery key and ${kit.codes.length} sign-in recovery codes.`
          : `One file with ${kit.codes.length} sign-in recovery codes. Add a data recovery key later in You › Security.`}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          className="!min-h-11"
          icon={<Download size={15} />}
          onClick={() => {
            downloadRecoveryKit(kit)
            setSaved(true)
          }}
        >
          Download kit
        </Button>
        {/* Kept in the DOM so the extension can find and enable it; shown only once it does. */}
        <span
          className={detected ? 'inline-flex' : 'sr-only'}
          aria-hidden={detected ? undefined : true}
          onPointerDown={wrapperProps.onPointerDown}
          onClickCapture={() => {
            wrapperProps.onClickCapture()
            if (detected) setSaved(true)
          }}
        >
          <button
            ref={button}
            type="button"
            tabIndex={detected ? 0 : -1}
            className="vk-btn !min-h-11"
            data-onepassword-save-button=""
            data-onepassword-save-request=""
            data-onepassword-type="login"
          >
            Save in 1Password
          </button>
        </span>
      </div>
      {extension === 'silent' ? (
        <p role="alert" className="m-0 text-[14px] text-status-fail">
          1Password did not respond. Download the kit instead.
        </p>
      ) : null}
      <details
        className="text-[14px]"
        onToggle={(e) => {
          if ((e.currentTarget as HTMLDetailsElement).open) setSaved(true)
        }}
      >
        <summary className="cursor-pointer text-accent-text">Show what’s in the kit</summary>
        <output
          aria-label="Recovery kit contents"
          className="mt-2 block whitespace-pre-wrap break-all rounded-lg bg-surface-2 p-3 font-mono text-[14px]"
        >
          {recoveryKitText(kit)}
        </output>
      </details>
      <p id={`${id}-help`} className="m-0 text-[14px] text-text-3">
        Keep it offline or in your password manager. It is the only way back to your encrypted work
        if you lose every device and passkey, and Viko shows it only now.
      </p>
      <Button
        variant={saved ? 'primary' : undefined}
        className="vk-btn--block !min-h-11"
        disabled={!saved}
        icon={<Check size={16} />}
        aria-describedby={`${id}-help`}
        onClick={onDone}
      >
        {doneLabel}
      </Button>
    </div>
  )
}

/** Pick a kit file or paste its text; resolves to the parsed kit (never echoes it in errors). */
export function KitInput({
  onKit,
  busy,
  submitLabel,
  children,
}: {
  onKit: (kit: ReturnType<typeof parseRecoveryKit>) => void | Promise<void>
  busy?: boolean
  submitLabel: string
  children?: ReactNode
}) {
  const id = useId()
  const file = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Never keep kit text around after the component goes away.
  useEffect(() => () => setText(''), [])
  const submit = async (value: string) => {
    setError(null)
    let kit: ReturnType<typeof parseRecoveryKit>
    try {
      kit = parseRecoveryKit(value)
    } catch {
      setError('That doesn’t look like a Viko recovery kit.')
      return
    }
    await onKit(kit)
  }
  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        void submit(text)
      }}
    >
      <input
        ref={file}
        type="file"
        accept=".txt,text/plain"
        className="sr-only"
        aria-label="Recovery kit file"
        onChange={async (e) => {
          const f = e.currentTarget.files?.[0]
          e.currentTarget.value = ''
          if (!f) return
          if (f.size > 16_384) {
            setError('That file is too large to be a Viko recovery kit.')
            return
          }
          await submit(await f.text())
        }}
      />
      <Button
        variant="primary"
        className="vk-btn--block !min-h-11"
        icon={<FileKey size={16} />}
        disabled={busy}
        onClick={() => file.current?.click()}
      >
        Open recovery kit file
      </Button>
      <label className="field" htmlFor={`${id}-text`}>
        <span>Or paste the kit</span>
      </label>
      <textarea
        id={`${id}-text`}
        className="input min-h-24 font-mono"
        autoComplete="off"
        spellCheck={false}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <Button type="submit" disabled={busy || !text.trim()}>
        {submitLabel}
      </Button>
      {error ? (
        <p role="alert" className="m-0 text-[15px] text-status-fail">
          {error}
        </p>
      ) : null}
      {children}
    </form>
  )
}
